import { useCallback, useEffect, useState, type FC } from "react";
import { StepSection } from "@components/StepSection";
import { StepTitleDescription } from "@/components/StepTitleDescritpion";
import styles from "./AddProductStep.module.css";
import { useTranslation } from "@/i18n";
import StepCard from "@/components/StepCard";
import {
  useConfigStore,
  useContactLensesStore,
  useStepStore,
  useStoreActions,
} from "@/store";
import SuggestionCard from "@/components/SuggestionCard";
import { useActionsModule } from "@/hooks/useActionsModule";
import { buildLensesDataToSend, formatDate } from "@/utils";
import { LensesObject } from "@/types";
import { getContactLensById } from "@/services/getContactLensById";
import { usePrescriptionData } from "@/hooks/usePrescriptionData";

interface AddProductStepProps {
  title: string;
  description?: string;
}

export const AddProductStep: FC<AddProductStepProps> = ({
  title = "title missing",
  description,
}) => {
  const { t } = useTranslation();
  const { config } = useConfigStore();
  const { goBackToPLP, genericAddToCart } = useActionsModule();
  const { contactLensesData, selectedPrescription } = useContactLensesStore();
  const { setLoading, setWidgetBlocked } = useStoreActions();
  const { isLoading } = useStepStore();
  const [
    suggestedProductFromPrescription,
    setSuggestedProductFromPrescription,
  ] = useState<any>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const eye = contactLensesData?.OD?.product ? "left" : "right";
  const [lensDataToSend, setLensDataToSend] = useState<LensesObject>();
  const { prescriptionAttributes } = usePrescriptionData();

  useEffect(() => {
    let updatedLensDataToSend = buildLensesDataToSend(
      contactLensesData,
      selectedPrescription,
    );
    if (
      config?.contactLensesData?.OD?.product &&
      updatedLensDataToSend.OD &&
      prescriptionAttributes?.OD
    ) {
      updatedLensDataToSend = {
        ...updatedLensDataToSend,
        OD: {
          ...updatedLensDataToSend.OD,
          prescription: prescriptionAttributes.OD,
        },
      };
    }
    if (
      config?.contactLensesData?.OS?.product &&
      updatedLensDataToSend.OS &&
      prescriptionAttributes?.OS
    ) {
      updatedLensDataToSend = {
        ...updatedLensDataToSend,
        OS: {
          ...updatedLensDataToSend.OS,
          prescription: prescriptionAttributes.OS,
        },
      };
    }
    setLensDataToSend(updatedLensDataToSend);
  }, [contactLensesData, prescriptionAttributes]);

  const stepTitle = t(title).replace(
    "###MISSING_EYE###",
    t(`steps.addProduct.${eye}`),
  );

  useEffect(() => {
    const fetchSuggested = async () => {
      if (!selectedPrescription || !config) {
        setSuggestedProductFromPrescription(null);
        return;
      }

      const productId =
        eye === "right"
          ? selectedPrescription.rightCatentryId
          : selectedPrescription.leftCatentryId;

      if (!productId) {
        setSuggestedProductFromPrescription(null);
        return;
      }

      try {
        const data = await getContactLensById(
          config.storeId,
          config.catalogId,
          productId.toString(),
          config.basePath,
        );

        setSuggestedProductFromPrescription({
          prescriptionID: selectedPrescription.prescriptionId,
          lastCreate: selectedPrescription.lastUpdate,
          ...data.catalogEntry,
        });
      } catch (error) {
        console.error("Error fetching suggested products:", error);
        setSuggestedProductFromPrescription(null);
      }
    };

    fetchSuggested();
  }, [selectedPrescription]);

  const handleAddProduct = useCallback(
    (productId: string) => {
      console.log("Adding product with ID:", productId);
      let dataToSend = { ...lensDataToSend };
      const newProductData = {
        productId,
        prescriptionId: suggestedProductFromPrescription.prescriptionID,
      };

      if (
        contactLensesData.hasOwnProperty("OD") &&
        dataToSend.OD &&
        prescriptionAttributes?.OS
      ) {
        dataToSend.OS = {
          ...newProductData,
          prescription: prescriptionAttributes.OS,
          boxQuantity: dataToSend.OD.boxQuantity,
        };
      } else if (
        contactLensesData.hasOwnProperty("OS") &&
        dataToSend.OS &&
        prescriptionAttributes?.OD
      ) {
        dataToSend.OD = {
          ...newProductData,
          prescription: prescriptionAttributes.OD,
          boxQuantity: dataToSend.OS.boxQuantity,
        };
      }
      console.log("Data to send for add to cart:", dataToSend);
      setLoading(true);
      genericAddToCart(dataToSend);
    },
    [lensDataToSend, suggestedProductFromPrescription, prescriptionAttributes],
  );

  const handleYesClick = () => {
    if (config && lensDataToSend) {
      console.log("Go back to PLP with data:", lensDataToSend);
      const cartMode = {
        orderId: config.orderId,
        OD: config?.contactLensesData?.OD?.product?.orderItemId
          ? { orderProductId: config.contactLensesData.OD.product.orderItemId }
          : undefined,
        OS: config?.contactLensesData?.OS?.product?.orderItemId
          ? { orderProductId: config.contactLensesData.OS.product.orderItemId }
          : undefined,
      };
      setIsConfirming(true);
      try {
        goBackToPLP(lensDataToSend, cartMode, config);
        setWidgetBlocked(true);
      } catch (error) {
        console.error("Error going back to PLP:", error);
        setIsConfirming(false);
      }
    }
  };

  return (
    <StepSection>
      <StepTitleDescription title={stepTitle} description={description} />
      <StepCard
        title={t("steps.addProduct.yes")}
        isLoading={isConfirming}
        disabled={isConfirming}
        onClick={handleYesClick}
      />
      <StepCard
        title={t("steps.addProduct.no")}
        disabled={isConfirming}
        onClick={() => {
          console.log("Generic add to cart with data:", lensDataToSend);
          if (lensDataToSend) {
            genericAddToCart(lensDataToSend);
          }
        }}
      />
      {suggestedProductFromPrescription && (
        <>
          <p className={styles.text}>
            {t("steps.addProduct.addFromPrescription")}
          </p>
          <SuggestionCard
            productName={suggestedProductFromPrescription.description[0]?.model}
            patientName={suggestedProductFromPrescription.prescriptionName}
            uploadedOnText={t("steps.addProduct.uploadedOnText")}
            date={formatDate(suggestedProductFromPrescription.lastCreate)}
            addLabel={t("steps.addProduct.add")}
            imageUrl={
              suggestedProductFromPrescription.description[0]?.thumbnail
            }
            onAddClick={() => {
              handleAddProduct(
                suggestedProductFromPrescription.catalogEntryIdentifier
                  .uniqueId,
              );
              setWidgetBlocked(true);
            }}
            isLoading={isLoading}
          />
        </>
      )}
    </StepSection>
  );
};
